import React from "react";
import PersonalInfoQuestion from "../intake/components/PersonalInfoQuestion";
import SexQuestion from "../intake/components/SexQuestion";
import MenstrualCycleQuestion from "../intake/components/MenstrualCycleQuestion";
import ContactInfoQuestion from "../intake/components/ContactInfoQuestion";
import AddressQuestion from "../intake/components/AddressQuestion";
import TreatmentQuestion from "../intake/components/TreatmentQuestion";
import DurationQuestion from "../intake/components/DurationQuestion";
import PaymentQuestion from "../intake/components/PaymentQuestion";
import StateQuestion from "../intake/components/StateQuestion";
import CreditCardQuestion from "../intake/components/CreditCardQuestion";
import PhotoUploadQuestion from "../intake/components/PhotoUploadQuestion";
import TextQuestion from "../intake/components/TextQuestion";
import OtherMedsQuestion from "../intake/components/OtherMedsQuestion";
import MaternityQuestion from "../intake/components/MaternityQuestion";
import TriedQuestion from "../intake/components/TriedQuestion";
import PrescriptionsQuestion from "../intake/components/PrescriptionsQuestion";
import MinorConsentQuestion from "../intake/components/MinorConsentQuestion";
import MinorSoloTreatmentQuestion from "../intake/components/MinorSoloTreatmentQuestion";
import MinorWeightQuestion from "../intake/components/MinorWeightQuestion";
import ParentInstructionsMessage from "../intake/components/ParentInstructionsMessage";
import InsuranceMethodQuestion from "../intake/components/InsuranceMethodQuestion";
import InsuranceInfoQuestion from "../intake/components/InsuranceInfoQuestion";
import InsurancePhotoQuestion from "../intake/components/InsurancePhotoQuestion";
import ReferralQuestion from "../intake/components/ReferralQuestion";
import ContraceptivesQuestion from "../intake/components/ContraceptivesQuestion";
import HasAllergiesQuestion from "../intake/components/HasAllergiesQuestion";

import { getAnswerById } from "../lib/helpers";

export function renderQuestionComponent({
    currentQuestion,
    questions,
    answers,
    onSubmit,
    onNext,
    setInfantBlocked,
    setCurrentQuestionIndex,
    visibleQuestions,
    ensureFullyVisible,
}) {
    if (!currentQuestion) return null;

    const commonProps = { onSubmit, onNext };
    const paymentAnswer = getAnswerById(answers, questions, "payment");
    const paymentType =
        paymentAnswer?.type === "insurance" ? "insurance" : "self-pay";

    const personal = getAnswerById(answers, questions, "personal-info");
    const contact = getAnswerById(answers, questions, "contact-info");
    const userForStripe = {
        firstName: personal?.name?.split?.(" ")?.[0] || personal?.firstName || "",
        lastName:
            personal?.name?.split?.(" ")?.slice(1).join(" ") ||
            personal?.lastName ||
            "",
        email: contact?.email || "",
        phoneNo: contact?.phoneNumber || "",
        dob: personal?.dateOfBirth || "",
        patientID: getAnswerById(answers, questions, "patient-id") || "",
        // Optional: mark returning patients if stored as an answer
        returning: Boolean(getAnswerById(answers, questions, "returning")),
    };

    if (currentQuestion.type === "system" || currentQuestion.hidden) return null;

    if (currentQuestion.type === "payment") {
        const stateQuestionIndex = questions.findIndex((q) => q.type === "state");
        const stateAnswer = stateQuestionIndex !== -1 ? answers[stateQuestionIndex] : null;
        commonProps.selectedState = stateAnswer || null;
    }

    switch (currentQuestion.type) {
        case "personal-info":
            return (
                <PersonalInfoQuestion
                    {...commonProps}
                    onInfantBlock={() => {
                        if (typeof setInfantBlocked === "function") setInfantBlocked(true);
                        if (typeof setCurrentQuestionIndex === "function") {
                            setCurrentQuestionIndex(visibleQuestions.length);
                        }
                    }}
                />
            );
        case "sex":
            return <SexQuestion {...commonProps} />;
        case "menstrual-cycle":
            return <MenstrualCycleQuestion {...commonProps} />;
        case "contraceptives":
            return (
                <ContraceptivesQuestion
                    {...commonProps}
                    initialValue={getAnswerById(answers, questions, "contraceptives")}
                    ensureFullyVisible={ensureFullyVisible}
                />
            );
        case "contact-info":
            return <ContactInfoQuestion {...commonProps} />;
        case "address":
            return (
                <AddressQuestion
                    {...commonProps}
                    user={{
                        email: contact?.email || "",
                        phoneNo: contact?.phoneNumber || "",
                    }}
                />
            );
        case "treatment":
            return <TreatmentQuestion {...commonProps} />;
        case "duration":
            return <DurationQuestion {...commonProps} />;
        case "payment":
            return <PaymentQuestion {...commonProps} />;
        case "state":
            return <StateQuestion {...commonProps} />;
        case "credit-card":
            return (
                <CreditCardQuestion
                    {...commonProps}
                    paymentType={paymentType} // 'self-pay' => $119/79 charge, 'insurance' => $1 auth
                    user={userForStripe}
                    initialValue={getAnswerById(answers, questions, "credit-card")}
                />
            );
        case "photo-upload":
            return (
                <PhotoUploadQuestion
                    {...commonProps}
                    answers={answers}
                    questions={questions}
                    ensureFullyVisible={ensureFullyVisible}
                // (optional) if you store previously uploaded urls in answers:
                // initialValue={getAnswerById(answers, questions, "patient-photos")}
                />
            );
        case "other-meds":
            return <OtherMedsQuestion {...commonProps} />;
        case "maternity": {
            const showBirthControl = false;
            return <MaternityQuestion {...commonProps} showBirthControl={showBirthControl} />;
        }
        case "tried":
            return (
                <TriedQuestion
                    {...commonProps}
                    initialValue={getAnswerById(answers, questions, "tried")}
                    ensureFullyVisible={ensureFullyVisible}
                />
            );
        case "prescriptions": {
            const treatmentAnswer = getAnswerById(answers, questions, "treatment");
            return (
                <PrescriptionsQuestion
                    {...commonProps}
                    initialValue={getAnswerById(answers, questions, "prescriptions")}
                    ensureFullyVisible={ensureFullyVisible}
                    treatmentAnswer={treatmentAnswer}
                />
            );
        }
        case "minor-consent":
            return <MinorConsentQuestion {...commonProps} />;
        case "minor-solo-treatment":
            return <MinorSoloTreatmentQuestion {...commonProps} />;
        case "minor-weight":
            return <MinorWeightQuestion {...commonProps} />;
        case "parent-instructions":
            return <ParentInstructionsMessage {...commonProps} />;
        case "insurance-method": {
            const payAns = getAnswerById(answers, questions, "payment");
            return (
                <InsuranceMethodQuestion
                    {...commonProps}
                    initialValue={getAnswerById(answers, questions, "insurance-method")}
                    selectedPayment={payAns}
                />
            );
        }
        case 'insurance-info':
            return <InsuranceInfoQuestion {...commonProps} initialValue={getAnswerById(answers, questions, 'insurance-info')} />;

        case 'insurance-photos':
            return <InsurancePhotoQuestion {...commonProps} initialValue={getAnswerById(answers, questions, 'insurance-photos')} />;

        case "referral":
            return <ReferralQuestion {...commonProps} question={currentQuestion} currentAnswer={getAnswerById(answers, questions, 'referral')} />;

        case "has-allergies":
            return <HasAllergiesQuestion {...commonProps} initialValue={getAnswerById(answers, questions, "has-allergies")} />;

        case "text":
            return <TextQuestion {...commonProps} placeholder={currentQuestion.placeholder} optional={currentQuestion.optional} />;
        default:
            return <TextQuestion {...commonProps} placeholder={currentQuestion.placeholder} optional={currentQuestion.optional} />;
    }
}
